import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Dict, Docmnt } from '/imports/types';
import { strings } from '/imports/strings';
import { MethodDeclaration } from '/imports/methods';
import { arrayToCsv, capitalize, clientDownload, defer, filterBoolean, formatDateTime, randomString, roundNumber } from '/imports/utils';
import { Table } from '/imports/ui/Table';
import { Checkbox } from '/imports/ui/Checkbox';
import { Select } from '/imports/ui/Select';
import { FormInput } from '/imports/ui/FormBuilder';
import { Button } from '/imports/ui/Button';
import { TableSkeleton } from '/imports/ui/TableSkeleton';
import { Icon } from '/imports/ui/Icon';

// Inputs whose value is committed the moment the user picks it (no blur/enter
// step). Other inputs (Input, NumberInput, TextArea, ...) commit on blur/enter.
const isImmediateCommitInput = (type: any) => type === Checkbox || type === Select;

export type Column<T extends Docmnt = any> = {
  field: keyof T;
  label?: string | (() => string | React.ReactNode);
  input?: FormInput;
  render?(d: T, r?: Dict<Dict<Docmnt>>): string | React.ReactNode;
  renderEditable?(d: T): React.ReactNode;
  width?: string;
  /**
   * Extra classes for this column's cells. `mono` is the one the kit defines:
   * Table ships `[&_td.mono]:font-mono [&_td.mono]:tabular-nums` and documented
   * it as a per-column opt-in, but there was no way to set a class on a cell, so
   * the rule matched nothing. Numeric columns should use it -- proportional
   * digits make a column of figures impossible to scan.
   */
  className?: string;
} | null

export interface DataTableProps<T extends Docmnt = any> {
  data: T[];
  references?: Dict<Dict<Docmnt>>;
  allIds?: string[];
  columns: Column<T>[];
  pageSize?: number;
  filters?: ((d: T) => boolean | null | undefined)[];
  updateMethod?: MethodDeclaration<Partial<T>, string>;
  deleteMethod?: MethodDeclaration<string, void>;
  sort?: { [k in keyof T]?: 1 | -1 };
  loading?: boolean;
  softLoading?: boolean;
  element?: (d: T) => React.ReactNode;
  row?: (d: T) => React.ReactNode;
  rowClass?: (d: T) => string;
  onSelectedIds?(selectedIds: string[]): void;
  onScrollBottom?(): void;
  onClickColumn?(column: Column): void;
  exportable?: boolean;
}

export const DataTable = <T extends Docmnt,>(props: DataTableProps<T>) => {
  const { data, references, columns, filters, pageSize, onScrollBottom, onSelectedIds, updateMethod,
    onClickColumn, loading, softLoading, element, row, rowClass, allIds, exportable } = props;
  const _pageSize = pageSize || 100;
  const _filters = filters || [];
  const [tableId,] = useState(`table-${randomString()}`);
  const [internalSort, setInternalSort] = useState<{ field: string; dir: 1 | -1 } | null>(null);
  const [limit, setLimit] = useState(_pageSize);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [enableEdit, setEnableEdit] = useState<Dict<{ [K in keyof T]: boolean }>>({});
  const [editing, setEditing] = useState<Dict<{ [K in keyof T]: any }>>({});
  const [edited, setEdited] = useState<Dict<{ [K in keyof T]: any }>>({});
  const tableRef = useRef<HTMLTableElement>(null);
  let filteredData = data;
  for (const filter of _filters) filteredData = filteredData.filter(filter);
  if (!onClickColumn && internalSort) {
    const { field, dir } = internalSort;
    filteredData = [...filteredData].sort((a, b) => {
      const av = (a as any)[field];
      const bv = (b as any)[field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }
  const toggleSort = (field: string) => {
    if (!internalSort || internalSort.field !== field) setInternalSort({ field, dir: 1 });
    else if (internalSort.dir === 1) setInternalSort({ field, dir: -1 });
    else setInternalSort(null);
  };
  useEffect(() => { onSelectedIds?.(selectedIds); }, [selectedIds]);
  // Keep the skeleton as tall as the data it stands in for, so reloading a
  // table does not resize the panel and bounce the page. First load has nothing
  // to match, so fall back to a short block -- NOT _pageSize, which is 100.
  const lastRowCount = useRef(0);
  useEffect(() => {
    if (!loading && filteredData.length) lastRowCount.current = filteredData.length;
  }, [loading, filteredData.length]);
  const skeletonRows = Math.min(Math.max(lastRowCount.current || 8, 3), 12);
  const [fixedWidths, setFixedWidths] = useState(false);
  useEffect(() => {
    if (fixedWidths) return;
    if (loading) return;
    if (!tableRef.current) return;
    const ths = Array.from(tableRef.current.querySelectorAll('th')).filter((th) => !th.style.width);
    for (const th of ths) th.style.width = `${roundNumber(th.getBoundingClientRect().width)}px`;
    setFixedWidths(true);
  }, [loading]);
  const focusInline = useCallback((_id: string, field: keyof T) => {
    defer(() => {
      const wrapper = document.getElementById(`inline_${_id}_${field as string}`);
      const focusable = wrapper?.querySelector<HTMLElement>('input, select, textarea');
      focusable?.focus();
    });
  }, []);
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const saveDocument = useCallback(async (_id: string, valuesOverride?: Dict<any>) => {
    const values = valuesOverride ?? editingRef.current[_id];
    if (!values) return;
    try {
      await updateMethod?.call({ ...values, _id } as any);
      setEdited((prev) => ({ ...prev, [_id]: values as any }));
      setEditing((prev) => { const n = { ...prev }; delete n[_id]; return n; });
      setEnableEdit((prev) => { const n = { ...prev }; delete n[_id]; return n; });
      toast.success(strings.Saved());
    }
    catch (error: any) {
      console.log(error);
      toast.error(`${error}`);
    }
  }, [updateMethod]);
  const columnSortClass = (field: string) => {
    if (!onClickColumn && internalSort?.field === field) return internalSort.dir === 1 ? 'sort-asc' : 'sort-desc';
    const sort = props.sort || {} as any;
    if (sort[field] === 1) return 'sort-asc';
    if (sort[field] === -1) return 'sort-desc';
    return '';
  };
  const exportToCsv = () => {
    const csv = arrayToCsv(filteredData);
    clientDownload(csv, `Export-${Date.now()}`, 'text/csv');
  };
  return (
    <Table onScrollBottom={onScrollBottom || (() => setLimit(limit + _pageSize))}>
      {/*
        Refresh-in-place indicator. The rail is h-0 and sticky so the bar
        overlays the top of the header band rather than pushing it down, and
        stays pinned while the body scrolls (an absolutely positioned bar would
        scroll away with the content, since Table is the scroll container).
        z-3 clears the sticky th at z-2.
      */}
      {softLoading && !loading ? (
        <div className="sticky top-0 z-[3] h-0" role="status" aria-label={strings.Loading()}>
          <div className="progress-indeterminate relative h-0.5 w-full overflow-hidden bg-primary/15" />
        </div>
      ) : null}
      {
        element ?
          <div className="custom-table-elements">
            {filteredData.map((d) => element(d))}
          </div>
          :
          <table ref={tableRef}>
            <thead>
              <tr>
                <th className="row-select">
                  <Checkbox onChange={(checked) => {
                    setSelectedIds(checked ? [...(allIds || [])] : []);
                  }} />
                </th>
                {
                  filterBoolean(columns).map((c) => {
                    const _label = typeof c.label === 'function' ? c.label() : c.label || (strings as any)[capitalize(c.field as string)]?.() || `_${String(c.field)}_`;
                    return (
                      <th key={c.field as string} className={columnSortClass(c.field as string)} style={{ width: c.width }} onClick={() => onClickColumn ? onClickColumn(c) : toggleSort(c.field as string)}>
                        {_label}
                      </th>
                    );
                  })
                }
                <th className="row-actions">
                  {
                    exportable !== false ? <span className="export-icon" onClick={() => exportToCsv()}><Icon icon="download" tip={strings.ExportToCsv()} /></span> : null
                  }
                </th>
              </tr>

            </thead>
            <tbody
              className={`${loading ? 'loading' : ''}${softLoading ? ' soft-loading' : ''}`}
              aria-busy={loading || softLoading || undefined}
            >
              {
                loading ?
                  <TableSkeleton columns={columns} rows={skeletonRows} />
                  :
                  filteredData.map((d) => {
                    return row?.(d) || (
                      <TableRow
                        key={String(d._id)}
                        d={d}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        enableEdit={enableEdit}
                        setEnableEdit={setEnableEdit as any}
                        editing={editing}
                        setEditing={setEditing as any}
                        edited={edited}
                        focusInline={focusInline as any}
                        saveDocument={saveDocument}
                        columns={columns}
                        rowClass={rowClass}
                        references={references}
                        tableId={tableId}
                        updateMethod={updateMethod}
                      />
                      // <tr key={d._id} className={rowClass?.(d) || ''}>
                      //   <td className="row-select">
                      //     <Checkbox
                      //       checked={selectedIds.includes(d._id)}
                      //       onChange={(checked) => {
                      //         // Todo: Handle shift key to bulk-select
                      //         if (checked) setSelectedIds([...selectedIds, d._id]);
                      //         else setSelectedIds(selectedIds.filter((_id) => _id !== d._id));
                      //       }}
                      //     />
                      //   </td>
                      //   {
                      //     filterBoolean(columns).map((c) => {
                      //       if ((c.renderEditable || c.input) && updateMethod && enableEdit[d._id]?.[c.field]) {
                      //         const _input = c.input as any;
                      //         return (
                      //           <td key={c.field as string}>
                      //             {
                      //               c.renderEditable ?
                      //                 c.renderEditable(d)
                      //                 :
                      //                 _input?.type === Checkbox ?
                      //                   React.createElement(Checkbox as any, {
                      //                     id: `inline_${d._id}_${c.field as string}`,
                      //                     checked: editing[d._id]?.[c.field],
                      //                     onChange: (value: any) => setEditing({ ...editing, [d._id]: { ...editing[d._id], [c.field]: value } }),
                      //                     ..._input?.props,
                      //                   })
                      //                   :
                      //                   React.createElement(_input?.type, {
                      //                     id: `inline_${d._id}_${c.field as string}`,
                      //                     value: editing[d._id]?.[c.field],
                      //                     onChange: (value: any) => setEditing({ ...editing, [d._id]: { ...editing[d._id], [c.field]: value } }),
                      //                     ..._input?.props,
                      //                   })
                      //             }
                      //           </td>
                      //         );
                      //       }
                      //       else {
                      //         const doc = { ...d, ...edited[d._id] };
                      //         const value = doc[c.field];
                      //         return (
                      //           <td key={c.field as string} onDoubleClick={() => {
                      //             if (!c.renderEditable && !c.input) return;
                      //             setEnableEdit({ ...editing, [d._id]: { ...editing[d._id], [c.field]: true } });
                      //             setEditing({ ...editing, [d._id]: { ...editing[d._id], [c.field]: value } });
                      //             focusInline(d._id, c.field);
                      //           }}>
                      //             {c.render ? c.render(doc, references) : (value as any instanceof Date ? formatDateTime(value) : value)}
                      //           </td>
                      //         );
                      //       }
                      //     })
                      //   }
                      //   <td className="row-actions">
                      //     {/* <a onClick={() => setDeletableDoc(user)}>
                      //   <Icon tip={strings.Deactivate()} icon="cancel" />
                      // </a> */}
                      //     {updateMethod && editing[d._id] && <Button onClick={() => saveDocument(d._id)}>{strings.Save()}</Button>}
                      //   </td>
                      // </tr>
                    );
                  })
              }
            </tbody>
          </table>
      }
    </Table>
  );
};

type TableRowProps<T extends Docmnt = any> = {
  d: T,
  selectedIds: string[],
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
  enableEdit: Dict<{ [K in keyof T]: boolean; }>,
  setEnableEdit: React.Dispatch<React.SetStateAction<Dict<{ [K in keyof T]: boolean; }>>>,
  editing: Dict<{ [K in keyof T]: any }>,
  setEditing: React.Dispatch<React.SetStateAction<Dict<{ [K in keyof T]: boolean; }>>>,
  edited: Dict<{ [K in keyof T]: any }>,
  focusInline: (_id: string, field: keyof T) => void,
  saveDocument: (_id: string, valuesOverride?: Dict<any>) => Promise<void>,
  tableId: string,
} & Pick<DataTableProps<T>, 'rowClass' | 'columns' | 'updateMethod' | 'references'>;

let scrollableViewport: Element | null;

const _TableRow = (props: TableRowProps) => {
  const {
    d, rowClass, references, columns, updateMethod, selectedIds, setSelectedIds, enableEdit, setEnableEdit, editing, setEditing,
    edited, focusInline, saveDocument, tableId
  } = props;
  const tr = useRef<HTMLTableRowElement | null>(null);
  scrollableViewport = scrollableViewport || document.getElementById(tableId);
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  if (!observerRef.current) {
    observerRef.current = new IntersectionObserver((changes) => {
      setIsVisible(changes[0]?.isIntersecting);
    }, {
      root: scrollableViewport,
      rootMargin: '50px 0px 50px 0px',
      threshold: 0,
    });
  }
  useEffect(() => {
    if (!tr.current || !observerRef.current) return;
    observerRef.current.observe(tr.current);
  }, [tr]);
  return (
    <tr ref={tr} key={d._id} className={rowClass?.(d) || ''}>
      {
        isVisible ?
          <td className="row-select">
            <Checkbox
              checked={selectedIds.includes(d._id)}
              onChange={(checked) => {
                // Todo: Handle shift key to bulk-select
                if (checked) setSelectedIds([...selectedIds, d._id]);
                else setSelectedIds(selectedIds.filter((_id) => _id !== d._id));
              }}
            />
          </td>
          :
          <td className="row-select" />
      }
      {
        filterBoolean(columns).map((c) => {
          if ((c.renderEditable || c.input) && updateMethod && enableEdit[d._id]?.[c.field as string]) {
            const _input = c.input as any;
            const inputType = _input?.type;
            const immediate = isImmediateCommitInput(inputType);
            const inputId = `inline_${d._id}_${c.field as string}`;
            const editingValues = editing[d._id] || {};
            const currentValue = editingValues[c.field as string];
            const onChange = (value: any) => {
              const next = { ...editingValues, [c.field]: value };
              setEditing((prev) => ({ ...prev, [d._id]: next }));
              if (immediate) saveDocument(d._id, next);
            };
            const onBlur = () => {
              if (immediate) return;
              saveDocument(d._id);
            };
            const onEnter = (value: any) => {
              if (immediate) return;
              const next = { ...editingValues, [c.field]: value };
              saveDocument(d._id, next);
            };
            return isVisible ?
              <td key={c.field as string} id={inputId} className={c.className}>
                {
                  c.renderEditable ?
                    c.renderEditable(d)
                    :
                    inputType === Checkbox ?
                      React.createElement(Checkbox as any, {
                        id: `${inputId}_input`,
                        checked: currentValue,
                        onChange,
                        ..._input?.props,
                      })
                      :
                      React.createElement(inputType, {
                        id: `${inputId}_input`,
                        value: currentValue,
                        onChange,
                        onBlur,
                        onEnter,
                        ..._input?.props,
                      })
                }
              </td>
              :
              <td key={c.field as string} className={c.className} />;
          }
          else {
            const doc = { ...d, ...edited[d._id] };
            const value = doc[c.field];
            return isVisible ?
              <td key={c.field as string} className={c.className} style={{ width: c.width }} onDoubleClick={() => {
                if (!c.renderEditable && !c.input) return;
                setEnableEdit((prev) => ({ ...prev, [d._id]: { ...(prev[d._id] || {}), [c.field]: true } }));
                setEditing((prev) => ({ ...prev, [d._id]: { ...(prev[d._id] || {}), [c.field]: value } }));
                focusInline(d._id, c.field);
              }}>
                {c.render ? c.render(doc, references) : (value as any instanceof Date ? formatDateTime(value) : value)}
              </td>
              :
              <td key={c.field as string} className={c.className} style={{ width: c.width }} />;
          }
        })
      }
      <td className="row-actions">
        {/* <a onClick={() => setDeletableDoc(user)}>
                        <Icon tip={strings.Deactivate()} icon="cancel" />
                      </a> */}
        {updateMethod && editing[d._id] && <Button onClick={() => saveDocument(d._id)}>{strings.Save()}</Button>}
      </td>
    </tr>
  );
};

const TableRow = React.memo(_TableRow);